import { useMemo } from 'react';

type NioshRisk = 'VERY_LOW' | 'LOW' | 'MODERATE' | 'HIGH' | 'VERY_HIGH';

interface NioshConclusionParams {
	mass_m: string | number;
	lifting_index: string | number;
	recommended_weight_limit: string | number;
	risk: NioshRisk;
}

interface NioshConclusionResult {
	text: string[];
	conclusion: string;
}

interface NioshRiskDefinition {
	rwlComparison: string;
	liRange: string;
	conclusion: string;
}

interface NioshConclusionTexts {
	VERY_LOW: NioshRiskDefinition;
	VERY_HIGH: NioshRiskDefinition;
	HIGH: NioshRiskDefinition;
	MODERATE: NioshRiskDefinition;
	LOW: NioshRiskDefinition;
}

const CONCLUSION_TEXTS: NioshConclusionTexts = {
	VERY_HIGH: {
		rwlComparison: 'is greater than the recommended weight limit (RWL)',
		liRange: 'is equal to or more than 3.',
		conclusion:
			' There is a very high risk. These values indicate that this lift would be hazardous for a majority of healthy industrial workers.'
	},
	HIGH: {
		rwlComparison: 'is greater than the recommended weight limit (RWL)',
		liRange: 'is equal to or more than 2 and less than 3.',
		conclusion:
			' There is a high risk. These values indicate that this lift would be hazardous for a majority of healthy industrial workers.'
	},
	MODERATE: {
		rwlComparison: 'is greater than the recommended weight limit (RWL)',
		liRange: 'is equal to or more than 1 and less than 2.',
		conclusion: ' There is a moderate risk. These values indicate that this job is somewhat stressful.'
	},
	LOW: {
		rwlComparison: 'is less than the recommended weight limit (RWL)',
		liRange: 'is less than 1.',
		conclusion:
			' There is a low risk. These values indicate that this job is adequate for the majority of industrial workers.'
	},
	VERY_LOW: {
		rwlComparison: '',
		liRange: '',
		conclusion: ''
	}
};

export function useNioshConclusion({
	mass_m,
	lifting_index,
	recommended_weight_limit,
	risk
}: NioshConclusionParams): NioshConclusionResult {
	return useMemo(() => {
		const { rwlComparison, liRange, conclusion } = CONCLUSION_TEXTS[risk];

		const text = [
			'The weight to be lifted',
			` [${mass_m} kg] `,
			rwlComparison,
			` [${recommended_weight_limit} kg].`,
			' The lifting index (LI)',
			` [${lifting_index}] `,
			liRange
		];

		return { text, conclusion };
	}, [mass_m, lifting_index, recommended_weight_limit, risk]);
}
